import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

function getToken(): string | null {
  return localStorage.getItem('admin_auth_token');
}

interface Props {
  userEmail?: string; // Pass the logged-in user's email to check if it's Gmail
}

export const GoogleCalendarSync: React.FC<Props> = ({ userEmail }) => {
  const [enabled, setEnabled] = useState(false);
  const [calendarEmail, setCalendarEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Check for ?calendar_connected=true or ?calendar_error on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected') === 'true') {
      toast.success('Google Calendar connected successfully!');
      params.delete('calendar_connected');
      window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`);
    } else if (params.get('calendar_error')) {
      toast.error('Failed to connect Google Calendar. Please try again.');
      params.delete('calendar_error');
      window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/calendar/status`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setEnabled(res.data.enabled);
      setCalendarEmail(res.data.calendarEmail || null);
    } catch (err) {
      console.error('Failed to fetch calendar status', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      // Pass current URL as returnUrl so the callback redirects back here
      const returnUrl = window.location.href.split('?')[0]; // strip existing query
      const res = await axios.get(`${API_BASE}/calendar/auth-url`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { returnUrl },
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to initiate Google Calendar connection';
      toast.error(msg);
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      await axios.post(`${API_BASE}/calendar/sync-all`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success('Sync started. Your events will appear in Google Calendar shortly.');
    } catch (err) {
      toast.error('Failed to start sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API_BASE}/calendar/disconnect`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success('Google Calendar disconnected');
      setEnabled(false);
      setCalendarEmail(null);
    } catch (err) {
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  // Only Gmail accounts can connect Google Calendar
  const isGmail = !userEmail || userEmail.toLowerCase().endsWith('@gmail.com');

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-48 mb-3"></div>
        <div className="h-3 bg-gray-100 rounded w-72"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
        <svg className="w-5 h-5 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5zm7 5h5v5h-5z" />
        </svg>
        Google Calendar Sync
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Automatically sync your assigned poojas and temple events to your personal Google Calendar.
      </p>

      {!isGmail ? (
        <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <svg className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <p className="text-sm text-gray-500">
            Google Calendar sync is only available for Gmail accounts (<code className="text-xs bg-gray-100 px-1 rounded">@gmail.com</code>).
            Your account uses <strong>{userEmail}</strong>.
          </p>
        </div>
      ) : enabled ? (
        <div>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mb-2">
            <span className="w-2 h-2 mr-1.5 bg-green-500 rounded-full"></span>
            Connected
          </span>
          {calendarEmail && (
            <p className="text-xs text-gray-500 mb-3">
              Syncing to: <span className="font-medium text-gray-700">{calendarEmail}</span>
            </p>
          )}
          <div className="flex items-center gap-4 mt-2">
            <button
              onClick={handleSyncAll}
              disabled={syncing}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {syncing ? 'Syncing...' : '🔄 Sync Now'}
            </button>
            <button
              onClick={handleDisconnect}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Disconnect Calendar
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={handleConnect}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5zm7 5h5v5h-5z"/>
          </svg>
          Connect Google Calendar
        </button>
      )}
    </div>
  );
};
